import { Crown, Eye, MoreHorizontal, Pencil, RotateCcw, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState, ErrorState, StatusPill } from '@/components/app';
import type { User } from '@/api/users';
import { USER_ROLE_LABELS, labelOf } from '@/lib/labels';
import { UsersTableProps } from '../types';
import { daysLeft, formatDate, formatDateTime, userDisplayName, userStatus } from '../format';

const COLUMNS = ['Пользователь', 'Роль', 'Premium до', 'Последний вход', 'Регистрация', 'Статус', 'Действия'];

export function UsersTable({
  users,
  isLoading,
  pageSize,
  selectedId,
  onView,
  onEdit,
  onDelete,
  onRestore,
  onGrantPremium,
  loadError,
  onRetry,
  hasFilters,
  onResetFilters,
}: UsersTableProps) {
  if (loadError) {
    return <ErrorState message={loadError} onRetry={onRetry} className="border-t border-border-soft" />;
  }

  if (!isLoading && users.length === 0) {
    return (
      <EmptyState
        className="border-t border-border-soft"
        icon={<Users />}
        title="Пользователи не найдены"
        description={hasFilters ? 'Попробуйте изменить или сбросить фильтры.' : undefined}
        action={
          hasFilters ? (
            <Button variant="secondary" onClick={onResetFilters}>
              Сбросить фильтры
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {COLUMNS.map((c) => (
            <TableHead key={c} className={c === 'Действия' ? 'w-px text-right' : undefined}>
              {c}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading
          ? Array.from({ length: Math.min(pageSize, 10) }, (_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="mt-1.5 h-3 w-52" />
                </TableCell>
                {COLUMNS.slice(1).map((c) => (
                  <TableCell key={c}>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                selected={user.id === selectedId}
                onView={onView}
                onEdit={onEdit}
                onDelete={onDelete}
                onRestore={onRestore}
                onGrantPremium={onGrantPremium}
              />
            ))}
      </TableBody>
    </Table>
  );
}

interface UserRowProps {
  user: User;
  selected: boolean;
  onView: (user: User) => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onRestore: (user: User) => void;
  onGrantPremium: (user: User) => void;
}

function UserRow({ user, selected, onView, onEdit, onDelete, onRestore, onGrantPremium }: UserRowProps) {
  const name = userDisplayName(user);
  const status = userStatus(user);
  const left = user.isSubscribed ? daysLeft(user.subscribedEnd) : null;
  const contact = [user.email, `@${user.username}`].filter(Boolean).join(' · ');

  return (
    <TableRow selected={selected} className="cursor-pointer" onClick={() => onView(user)}>
      <TableCell className="max-w-[320px]">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView(user);
          }}
          className="flex min-w-0 flex-col items-start rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="truncate font-semibold text-text">{name}</span>
          <span className="max-w-full truncate text-[13px] text-text-2">{contact}</span>
        </button>
      </TableCell>
      <TableCell>
        <span className="flex flex-wrap gap-1.5">
          <StatusPill size="sm" tone={user.role === 'admin' ? 'info' : 'neutral'}>
            {labelOf(USER_ROLE_LABELS, user.role)}
          </StatusPill>
          {user.is_anonymous && (
            <StatusPill size="sm" tone="warn">
              Аноним
            </StatusPill>
          )}
        </span>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {user.isSubscribed ? (
          <span className="flex flex-col">
            <span className="tabular-nums">{user.subscribedEnd ? formatDate(user.subscribedEnd) : 'Premium'}</span>
            <span className="text-[13px] text-text-2">{left !== null ? `осталось ${left} дн.` : 'срок истёк'}</span>
          </span>
        ) : (
          <span className="flex flex-col">
            <span>—</span>
            <span className="text-[13px] text-text-2">Free</span>
          </span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap tabular-nums text-text-2">{formatDateTime(user.lastLogin)}</TableCell>
      <TableCell className="whitespace-nowrap tabular-nums text-text-2">{formatDate(user.created_at)}</TableCell>
      <TableCell>
        <StatusPill tone={status.tone} dot>
          {status.label}
        </StatusPill>
      </TableCell>
      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={`Действия: ${name}`}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onView(user)}>
              <Eye />
              Открыть карточку
            </DropdownMenuItem>
            {!user.isDeleted && (
              <>
                <DropdownMenuItem onSelect={() => onEdit(user)}>
                  <Pencil />
                  Редактировать
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onGrantPremium(user)}>
                  <Crown />
                  Выдать Premium…
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            {user.isDeleted ? (
              <DropdownMenuItem onSelect={() => onRestore(user)}>
                <RotateCcw />
                Восстановить аккаунт…
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem destructive onSelect={() => onDelete(user)}>
                <Trash2 />
                Удалить аккаунт…
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
